
import React, { useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Brain,
  CheckCircle,
  Cpu,
  FileText,
  Info,
  Layers,
  Upload,
  Zap,
} from 'lucide-react';

type OverlayMode = 'segmentation' | 'heatmap';
type ViewMode = 'analysis' | 'report';

type SeverityKey = 'severe' | 'moderate' | 'mild' | 'normal';

type ToothMetrics = {
  boneDensity: number;
  cariesRisk: number;
  pdlWidth: string;
  enamelIntegrity: number;
};

type ToothTreatment = {
  investigation: string;
  conservative: string;
  surgical: string;
};

type ToothAnalysis = {
  id: number;
  tooth: string;
  name: string;
  path: string;
  center: { x: number; y: number };
  confidence: string;
  measurement: string;
  type: 'success' | 'warning' | 'info';
  condition: string;
  metrics: ToothMetrics;
  crown: string;
  restorative: string;
  pulpal: string;
  root: string;
  periapical: string;
  perio: string;
  relation: string;
  flags: string[];
  radiographicEvidence: string;
  treatment: ToothTreatment;
  analysis: string;
};

const DentalAnalyzer: React.FC = () => {
  const [selectedTooth, setSelectedTooth] = useState<ToothAnalysis | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [showMeasurements] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('analysis');
  const [isProcessing, setIsProcessing] = useState(false);
  const [overlayMode, setOverlayMode] = useState<OverlayMode>('segmentation');

  const severityScale: { key: SeverityKey; label: string; min: number; color: string; fill: string }[] = [
    { key: 'severe', label: 'Severe', min: 70, color: '#ef4444', fill: 'rgba(239, 68, 68, 0.32)' },
    { key: 'moderate', label: 'Moderate', min: 40, color: '#f97316', fill: 'rgba(249, 115, 22, 0.28)' },
    { key: 'mild', label: 'Mild', min: 20, color: '#eab308', fill: 'rgba(234, 179, 8, 0.24)' },
    { key: 'normal', label: 'Normal', min: 0, color: '#22c55e', fill: 'rgba(34, 197, 94, 0.18)' },
  ];

  const fullDentition = useMemo(() => {
    const teeth: ToothAnalysis[] = [];

    const createTooth = (
      id: number,
      name: string,
      x: number,
      y: number,
      w: number,
      h: number,
      condition = 'Healthy',
      risk = 'Low',
      density = 1200
    ): ToothAnalysis => {
      const detailedProfile = generateClinicalProfile(id, condition);

      return {
        id,
        tooth: `${id} (${name})`,
        name,
        path: getToothPath(id, x, y, w, h),
        center: { x, y },
        confidence: `${(90 + Math.random() * 9).toFixed(1)}%`,
        measurement: `${(8 + Math.random() * 4).toFixed(1)}mm`,
        type:
          condition === 'Healthy'
            ? 'success'
            : condition.includes('Impaction') || condition.includes('RCT')
            ? 'warning'
            : 'info',
        condition,
        metrics: {
          boneDensity: density + Math.floor(Math.random() * 200 - 100),
          cariesRisk: risk === 'High' ? 85 : risk === 'Medium' ? 45 : 12,
          pdlWidth: (0.15 + Math.random() * 0.1).toFixed(2),
          enamelIntegrity: condition === 'Healthy' ? 98 : 75,
        },
        ...detailedProfile,
        analysis: detailedProfile.radiographicEvidence,
      };
    };
    teeth.push(createTooth(48, 'LR Wisdom', 13.5, 66, 6, 8, 'Vertical Impaction', 'Medium', 1100));
    teeth.push(createTooth(47, 'LR 2nd Molar', 19, 68, 6, 9, 'Restored', 'Low', 1350));
    teeth.push(createTooth(46, 'LR 1st Molar', 25.5, 68, 7, 10, 'RCT & Crown', 'High', 2100));
    teeth.push(createTooth(45, 'LR 2nd Premolar', 31, 69, 4, 9));
    teeth.push(createTooth(44, 'LR 1st Premolar', 35, 70, 4, 9));
    teeth.push(createTooth(43, 'LR Canine', 39, 71, 4, 11));
    teeth.push(createTooth(42, 'LR Lateral', 42, 72, 3.5, 10));
    teeth.push(createTooth(41, 'LR Central', 45, 72, 3.5, 9));

    teeth.push(createTooth(31, 'LL Central', 48, 72, 3.5, 9));
    teeth.push(createTooth(32, 'LL Lateral', 51, 72, 3.5, 10));
    teeth.push(createTooth(33, 'LL Canine', 55, 71, 4, 11));
    teeth.push(createTooth(34, 'LL 1st Premolar', 60, 70, 4, 9));
    teeth.push(createTooth(35, 'LL 2nd Premolar', 65, 69, 4, 9));
    teeth.push(createTooth(36, 'LL 1st Molar', 71, 68, 7, 10, 'Heavily Restored', 'Medium', 1800));
    teeth.push(createTooth(37, 'LL 2nd Molar', 78, 67, 6, 9, 'Restored', 'Low'));
    teeth.push(createTooth(38, 'LL Wisdom', 85, 64, 8, 8, 'Horizontal Impaction', 'High', 1150));

    teeth.push(createTooth(18, 'UR Wisdom', 14, 32, 5, 7, 'Missing/Unerupted', 'N/A', 0));
    teeth.push(createTooth(17, 'UR 2nd Molar', 20, 34, 6, 8, 'Restored', 'Low'));
    teeth.push(createTooth(16, 'UR 1st Molar', 28, 35, 7, 9, 'Heavily Restored', 'Medium'));
    teeth.push(createTooth(15, 'UR 2nd Premolar', 34, 38, 4, 8));
    teeth.push(createTooth(14, 'UR 1st Premolar', 38, 39, 4, 8));
    teeth.push(createTooth(13, 'UR Canine', 42, 40, 4, 10));

    teeth.push(createTooth(23, 'UL Canine', 52, 40, 4, 10));
    teeth.push(createTooth(24, 'UL 1st Premolar', 56, 39, 4, 8));
    teeth.push(createTooth(25, 'UL 2nd Premolar', 61, 38, 4, 8));
    teeth.push(createTooth(26, 'UL 1st Molar', 67, 36, 7, 9, 'Deep Restoration', 'Medium'));
    teeth.push(createTooth(27, 'UL 2nd Molar', 74, 34, 6, 8));
    teeth.push(createTooth(28, 'UL Wisdom', 80, 32, 5, 7, 'Erupted', 'Low'));

    return teeth;
  }, []);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setIsProcessing(true);
      setTimeout(() => {
        const result = e.target?.result;
        setImageSrc(typeof result === 'string' ? result : null);
        setIsProcessing(false);
      }, 1500);
    };
    reader.readAsDataURL(file);
  };

  const getSeverity = (item: ToothAnalysis) => {
    const level = severityScale.find(entry => item.metrics.cariesRisk >= entry.min) ?? severityScale[severityScale.length - 1];
    return level;
  };

  const severitySummary = useMemo(() => {
    const counts: Record<SeverityKey, number> = {
      severe: 0,
      moderate: 0,
      mild: 0,
      normal: 0,
    };

    fullDentition.forEach(item => {
      const severity = getSeverity(item);
      counts[severity.key] += 1;
    });

    return counts;
  }, [fullDentition]);

  const findingsBySeverity = useMemo(() => {
    const ranked = [...fullDentition]
      .map(item => ({ item, severity: getSeverity(item) }))
      .sort((a, b) => {
        if (b.severity.min !== a.severity.min) return b.severity.min - a.severity.min;
        return b.item.metrics.cariesRisk - a.item.metrics.cariesRisk;
      });
    return ranked;
  }, [fullDentition]);

  const getPathStyle = (item: ToothAnalysis, isSelected: boolean) => {
    const severity = getSeverity(item);
    let strokeColor = severity.color;
    let fillColor = severity.fill;

    if (overlayMode === 'heatmap') {
      const intensity = item.metrics.cariesRisk / 100;
      fillColor = `rgba(255, ${255 - intensity * 255}, 0, ${0.3 + intensity * 0.4})`;
      strokeColor = `rgba(255, ${255 - intensity * 255}, 0, 0.8)`;
      if (intensity < 0.2) {
        fillColor = 'rgba(0,0,0,0)';
        strokeColor = 'rgba(255,255,255,0.1)';
      }
    }

    if (isSelected) {
      fillColor = 'rgba(255, 255, 255, 0.2)';
      strokeColor = '#ffffff';
    }

    return { fill: fillColor, stroke: strokeColor, strokeWidth: isSelected ? '0.6' : '0.3' };
  };

  if (viewMode === 'report') {
    return <ReportView fullDentition={fullDentition} imageSrc={imageSrc} onBack={() => setViewMode('analysis')} />;
  }

  return (
    <div className="relative flex flex-col min-h-[calc(100vh-160px)] bg-black text-slate-100 font-sans rounded-3xl overflow-hidden">
      <header className="bg-slate-900/90 backdrop-blur-md border-b border-slate-800 px-6 py-3 flex items-center justify-between z-20 absolute top-0 w-full">
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-2 rounded-lg shadow-lg shadow-blue-900/50">
            <Brain className="text-white w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white tracking-tight leading-none">
              DentalMetric <span className="text-blue-400">Ultra</span>
            </h1>
            <p className="text-[9px] text-slate-400 font-mono uppercase tracking-widest mt-1">Full Arch Analytics Engine</p>
          </div>
          <div className="hidden md:flex items-center px-2 py-1 rounded-full border border-slate-700 text-[10px] text-slate-300 uppercase tracking-widest">
            FDI / ISO 3950 numbering
          </div>
        </div>

        <div className="flex bg-slate-800 rounded-lg p-1 border border-slate-700">
          <button
            onClick={() => setOverlayMode('segmentation')}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${overlayMode === 'segmentation' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
          >
            Segmentation
          </button>
          <button
            onClick={() => setOverlayMode('heatmap')}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${overlayMode === 'heatmap' ? 'bg-red-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
          >
            Risk Heatmap
          </button>
        </div>

        <div className="flex gap-3">
          <label className="flex items-center gap-2 px-4 py-2 bg-white text-black hover:bg-slate-200 rounded-lg cursor-pointer transition-all text-xs font-bold shadow-lg shadow-white/10 uppercase tracking-wide">
            <Upload className="w-3.5 h-3.5" />
            Upload X-Ray
            <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
          </label>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden pt-16">
        <div className="flex-1 relative bg-black overflow-hidden flex items-center justify-center">
          {isProcessing ? (
            <ProcessingScreen />
          ) : !imageSrc ? (
            <UploadPrompt onUpload={handleImageUpload} />
          ) : (
            <div className="relative w-full h-full p-4 flex items-center justify-center bg-black/95">
              <div className="relative w-full max-w-[1400px] aspect-[16/9] border border-slate-800 rounded-xl overflow-hidden shadow-2xl bg-black">
                <img src={imageSrc} alt="Dental X-Ray" className="w-full h-full object-contain opacity-60 grayscale-[30%]" />

                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {fullDentition.map((item) => {
                    const isSelected = selectedTooth?.id === item.id;
                    const style = getPathStyle(item, isSelected);
                    const severity = getSeverity(item);
                    const highlightStroke = severity.color;

                    if (overlayMode === 'heatmap' && item.metrics.cariesRisk < 20 && !isSelected) return null;

                    return (
                      <g key={item.id} className="cursor-pointer group" onClick={() => setSelectedTooth(item)}>
                        {severity.key !== 'normal' && (
                          <path
                            d={item.path}
                            fill={severity.fill}
                            stroke="none"
                            vectorEffect="non-scaling-stroke"
                          />
                        )}
                        <path
                          d={item.path}
                          fill={style.fill}
                          stroke={style.stroke}
                          strokeWidth={style.strokeWidth}
                          vectorEffect="non-scaling-stroke"
                          className="transition-all duration-200"
                        />
                        {(isSelected || severity.key !== 'normal') && (
                          <circle
                            cx={item.center.x}
                            cy={item.center.y}
                            r={isSelected ? 4.2 : 3.6}
                            fill="none"
                            stroke={isSelected ? '#ffffff' : highlightStroke}
                            strokeWidth={isSelected ? '0.6' : '0.4'}
                            opacity={isSelected ? 0.95 : 0.7}
                          />
                        )}
                        {(isSelected || (severity.key !== 'normal' && showMeasurements)) && (
                          <g transform={`translate(${item.center.x}, ${item.center.y - 6})`}>
                            <rect x="-6" y="-3" width="12" height="6" rx="1" fill="rgba(0,0,0,0.8)" stroke={style.stroke} strokeWidth="0.1" />
                            <text y="0" textAnchor="middle" fill="white" fontSize="2" fontWeight="bold" fontFamily="monospace">
                              #{item.id}
                            </text>
                            <text y="2.2" textAnchor="middle" fill={style.stroke} fontSize="1.5" fontFamily="monospace">
                              {severity.label.toUpperCase()}
                            </text>
                          </g>
                        )}
                      </g>
                    );
                  })}

                  {selectedTooth && (
                    <g className="pointer-events-none animate-bounce-custom">
                      <line
                        x1={selectedTooth.center.x}
                        y1={selectedTooth.center.y - 12}
                        x2={selectedTooth.center.x}
                        y2={selectedTooth.center.y - 2}
                        stroke="#60a5fa"
                        strokeWidth="0.6"
                        strokeLinecap="round"
                      />
                      <path
                        d={`M ${selectedTooth.center.x},${selectedTooth.center.y - 2}
                          L ${selectedTooth.center.x - 2},${selectedTooth.center.y - 5}
                          L ${selectedTooth.center.x + 2},${selectedTooth.center.y - 5} Z`}
                        fill="#60a5fa"
                      />
                      <circle cx={selectedTooth.center.x} cy={selectedTooth.center.y} r="0.6" fill="none" stroke="#60a5fa" strokeWidth="0.3" />
                    </g>
                  )}
                </svg>
              </div>
            </div>
          )}
        </div>

        <div
          className={`w-96 bg-slate-900 border-l border-slate-800 flex flex-col transition-transform duration-300 shadow-2xl z-30 absolute right-0 h-full pt-16 ${selectedTooth ? 'translate-x-0' : 'translate-x-full'} md:relative md:translate-x-0 md:pt-0`}
        >
          {selectedTooth ? (
            <div className="flex flex-col h-full">
              <div className="p-6 border-b border-slate-800 bg-slate-900">
                <div className="flex justify-between items-start mb-2">
                  <div className="px-2 py-0.5 rounded bg-blue-900/30 border border-blue-500/30 text-blue-400 text-[10px] font-mono font-bold uppercase tracking-wider">
                    ISO {selectedTooth.id}
                  </div>
                  <button onClick={() => setSelectedTooth(null)} className="md:hidden text-slate-400">
                    <XIcon />
                  </button>
                </div>
                <h2 className="text-xl font-bold text-white mb-1">{selectedTooth.name}</h2>
                <p className="text-sm text-slate-400">{selectedTooth.condition}</p>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className="space-y-3">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Severity Overview</h3>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    {severityScale.map(level => (
                      <div key={level.key} className="rounded-lg border border-slate-800 bg-slate-900/50 p-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold" style={{ color: level.color }}>{level.label}</span>
                          <span className="text-slate-300">{severitySummary[level.key]}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Findings by Severity</h3>
                  <div className="space-y-2">
                    {findingsBySeverity.slice(0, 8).map(({ item, severity }) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setSelectedTooth(item)}
                        className="w-full text-left p-2 rounded-lg border border-slate-800 bg-slate-900/50 hover:bg-slate-800/70 transition"
                      >
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-bold" style={{ color: severity.color }}>
                            Tooth {item.id} · {severity.label}
                          </span>
                          <span className="text-slate-400">{item.metrics.cariesRisk}%</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1 truncate">{item.condition}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                    <BarChart3 className="w-3.5 h-3.5" /> Clinical Metrics
                  </h3>

                  <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-400">Est. Bone Density</span>
                      <span className="text-white font-mono">{selectedTooth.metrics.boneDensity} HU</span>
                    </div>
                    <div className="w-full bg-slate-700 h-1.5 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500" style={{ width: `${(selectedTooth.metrics.boneDensity / 2500) * 100}%` }} />
                    </div>
                  </div>

                  <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-400">Caries Probability</span>
                      <span className={`font-mono ${selectedTooth.metrics.cariesRisk > 50 ? 'text-red-400' : 'text-green-400'}`}>
                        {selectedTooth.metrics.cariesRisk}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 h-1.5 rounded-full overflow-hidden">
                      <div className={`h-full ${selectedTooth.metrics.cariesRisk > 50 ? 'bg-red-500' : 'bg-green-500'}`} style={{ width: `${selectedTooth.metrics.cariesRisk}%` }} />
                    </div>
                  </div>
                </div>

                <div className="bg-blue-900/10 p-4 rounded-xl border border-blue-500/20">
                  <h3 className="text-xs font-bold text-blue-400 uppercase mb-2 flex items-center gap-2">
                    <Zap className="w-3.5 h-3.5" /> AI Diagnostic Model
                  </h3>
                  <p className="text-slate-300 text-xs leading-relaxed font-mono opacity-90">{selectedTooth.analysis}</p>
                </div>

                {selectedTooth.condition !== 'Healthy' && (
                  <div className="space-y-2">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Flags</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedTooth.flags.map((flag) => (
                        <span key={flag} className="px-2 py-1 bg-red-500/10 text-red-400 border border-red-500/20 rounded text-[10px] uppercase font-bold">
                          {flag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-slate-800 bg-slate-900">
                <button
                  onClick={() => setViewMode('report')}
                  className="w-full py-3 px-4 bg-white hover:bg-slate-200 text-black rounded-lg font-bold text-xs uppercase tracking-wide transition-colors flex items-center justify-center gap-2"
                >
                  <FileText className="w-3.5 h-3.5" /> Add to Final Report
                </button>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-8 text-center text-slate-500">
              <div className="relative mb-6">
                <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center border border-slate-700">
                  <Layers className="w-8 h-8 text-slate-600" />
                </div>
                <div className="absolute -bottom-1 -right-1 bg-green-500 w-6 h-6 rounded-full border-4 border-slate-900" />
              </div>
              <h3 className="text-white font-bold mb-2">32-Point Analysis Active</h3>
              <p className="text-xs text-slate-400 max-w-[200px]">
                Select any tooth on the arch to view deep-learning metrics and structural analytics.
              </p>
              <div className="mt-6 w-full space-y-3">
                <h4 className="text-[11px] uppercase text-slate-500 font-bold tracking-widest">Severity Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  {severityScale.map(level => (
                    <div key={level.key} className="rounded-lg border border-slate-800 bg-slate-900/50 p-2">
                      <div className="flex items-center justify-between">
                        <span className="font-bold" style={{ color: level.color }}>{level.label}</span>
                        <span className="text-slate-300">{severitySummary[level.key]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes bounceCustom {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15%); }
        }
        .animate-bounce-custom {
          animation: bounceCustom 1.5s infinite;
        }
      `}</style>
    </div>
  );
};

type ReportViewProps = {
  fullDentition: ToothAnalysis[];
  imageSrc: string | null;
  onBack: () => void;
};

const ReportView: React.FC<ReportViewProps> = ({ fullDentition, imageSrc, onBack }) => {
  const pathologies = fullDentition.filter(t => t.condition !== 'Healthy');

  return (
    <div className="min-h-[calc(100vh-160px)] bg-white text-slate-900 font-sans p-8 overflow-y-auto rounded-3xl">
      <div className="max-w-6xl mx-auto border border-slate-200 shadow-2xl bg-white min-h-[1000px]">
        <div className="p-10 border-b border-slate-100 flex justify-between items-start bg-slate-900 text-white">
          <div className="flex gap-5 items-center">
            <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-xl">
              <Activity className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">DentalMetric Ultra</h1>
              <p className="text-sm text-blue-200 uppercase tracking-widest font-medium mt-1">Comprehensive Radiology Report</p>
            </div>
          </div>
          <div className="text-right text-sm text-slate-300">
            <p className="font-bold text-white mb-1">PATIENT REF: #8821-OPG</p>
            <p>Date: {new Date().toLocaleDateString()}</p>
            <p>Referring Dr: Dr. S. Smith</p>
          </div>
        </div>

        <div className="bg-slate-50 px-10 py-4 border-b border-slate-200 flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide font-bold">
          <Info className="w-4 h-4" />
          Tooth Identification System: FDI World Dental Federation Notation (ISO 3950)
        </div>

        <div className="p-10">
          <div className="mb-12">
            <h3 className="text-lg font-bold text-slate-900 uppercase mb-4 border-b border-slate-200 pb-2 flex items-center gap-2">
              <ScanLineIcon />
              Radiographic Overview
            </h3>
            <div className="relative w-full aspect-[2.5/1] bg-black rounded-lg overflow-hidden border border-slate-200 shadow-lg">
              {imageSrc && <img src={imageSrc} className="w-full h-full object-contain opacity-80" />}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                {fullDentition.map(item => (
                  item.condition !== 'Healthy' && (
                    <g key={item.id}>
                      <path d={item.path} fill="rgba(239, 68, 68, 0.4)" stroke="red" strokeWidth="0.5" />
                      <path
                        d={`M ${item.center.x},${item.center.y - 15} L ${item.center.x},${item.center.y - 5} L ${item.center.x - 1},${item.center.y - 8} M ${item.center.x},${item.center.y - 5} L ${item.center.x + 1},${item.center.y - 8}`}
                        fill="none"
                        stroke="red"
                        strokeWidth="0.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <text x={item.center.x} y={item.center.y - 18} fill="white" fontSize="2.5" fontWeight="bold" textAnchor="middle">
                        {item.id}
                      </text>
                    </g>
                  )
                ))}
              </svg>
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-lg font-bold text-slate-900 uppercase mb-6 border-b border-slate-200 pb-2 flex items-center gap-2">
              <ClipboardListIcon />
              Detailed Radiographic Assessment
            </h3>
            <div className="space-y-8">
              {pathologies.map((tooth) => (
                <div key={tooth.id} className="bg-slate-50 rounded-xl border border-slate-200 p-6 shadow-sm">
                  <div className="flex justify-between items-start mb-4 border-b border-slate-200 pb-4">
                    <div>
                      <h4 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                        Tooth {tooth.id}
                        <span className="text-sm font-normal text-slate-500">({tooth.name})</span>
                      </h4>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold uppercase ${
                        tooth.type === 'warning' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        Flagged: {tooth.condition}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500 uppercase tracking-wide">Confidence</div>
                      <div className="font-mono font-bold text-slate-700">{tooth.confidence}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                    <div className="space-y-3">
                      <div className="flex justify-between border-b border-slate-200/50 pb-1">
                        <span className="text-slate-500">Crown Integrity</span>
                        <span className="font-medium text-slate-900">{tooth.crown}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-200/50 pb-1">
                        <span className="text-slate-500">Restorative Status</span>
                        <span className="font-medium text-slate-900">{tooth.restorative}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-200/50 pb-1">
                        <span className="text-slate-500">Pulpal Status</span>
                        <span className="font-medium text-slate-900">{tooth.pulpal}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-200/50 pb-1">
                        <span className="text-slate-500">Root Morphology</span>
                        <span className="font-medium text-slate-900">{tooth.root}</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between border-b border-slate-200/50 pb-1">
                        <span className="text-slate-500">Periapical Region</span>
                        <span className="font-medium text-slate-900">{tooth.periapical}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-200/50 pb-1">
                        <span className="text-slate-500">Bone Support</span>
                        <span className="font-medium text-slate-900">{tooth.perio}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-200/50 pb-1">
                        <span className="text-slate-500">Adjacent Structures</span>
                        <span className="font-medium text-slate-900">{tooth.relation}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 bg-white p-4 rounded-lg border border-slate-200">
                    <div className="mb-4">
                      <h5 className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-red-500" /> Interpretation & Flags
                      </h5>
                      <ul className="list-disc pl-4 space-y-1 text-slate-700">
                        {tooth.flags.map((flag) => (
                          <li key={flag}>{flag}</li>
                        ))}
                        <li><strong>Radiographic Evidence:</strong> {tooth.radiographicEvidence}</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-500" /> Treatment Suggestions
                      </h5>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="block font-bold text-slate-700 mb-1">Investigations</span>
                          {tooth.treatment.investigation}
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="block font-bold text-slate-700 mb-1">Conservative</span>
                          {tooth.treatment.conservative}
                        </div>
                        <div className="bg-slate-50 p-2 rounded">
                          <span className="block font-bold text-slate-700 mb-1">Surgical/Prognosis</span>
                          {tooth.treatment.surgical}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-lg font-bold text-slate-900 uppercase mb-4 border-b border-slate-200 pb-2 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Clinical Summary
            </h3>
            <table className="w-full text-sm text-left border-collapse border border-slate-200">
              <thead>
                <tr className="bg-slate-100 text-slate-700 uppercase text-xs tracking-wide">
                  <th className="py-3 px-4 border-b border-slate-200 font-bold">Tooth</th>
                  <th className="py-3 px-4 border-b border-slate-200 font-bold">Suspected Issue</th>
                  <th className="py-3 px-4 border-b border-slate-200 font-bold">Severity</th>
                  <th className="py-3 px-4 border-b border-slate-200 font-bold">Recommended Step</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pathologies.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="py-3 px-4 border-r border-slate-100 font-bold text-slate-900">{item.id}</td>
                    <td className="py-3 px-4 border-r border-slate-100 text-slate-700">{item.condition}</td>
                    <td className="py-3 px-4 border-r border-slate-100">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                        item.metrics.cariesRisk > 70 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {item.metrics.cariesRisk > 70 ? 'Severe' : 'Moderate'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-700">{item.treatment.investigation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 text-xs text-slate-500">
            <h4 className="font-bold text-slate-700 uppercase mb-2">Important Disclaimers</h4>
            <ul className="list-disc pl-4 space-y-1">
              <li>These findings are based on AI-assisted radiographic interpretation of a 2D panoramic image and do not constitute a definitive medical diagnosis.</li>
              <li>Panoramic radiography has inherent limitations including magnification, distortion, and superimposition of anatomical structures.</li>
              <li><strong>Clinical Correlation Required:</strong> All radiographic findings must be verified clinically. Vitality testing is essential for any tooth with deep restorations or suspected pulpal pathology.</li>
              <li>Periodontal bone levels on OPG may underestimate actual bone loss. Bitewing radiographs are recommended for accurate caries and bone level assessment.</li>
            </ul>
          </div>
        </div>

        <div className="p-10 border-t border-slate-100 bg-slate-50 flex justify-end gap-4 sticky bottom-0">
          <button onClick={() => window.print()} className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-bold shadow-lg">
            <PrinterIcon /> Print Report
          </button>
          <button onClick={onBack} className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-bold">
            <ArrowLeft className="w-4 h-4" /> Return to Analytics
          </button>
        </div>
      </div>
    </div>
  );
};

type UploadPromptProps = {
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const UploadPrompt: React.FC<UploadPromptProps> = ({ onUpload }) => (
  <div className="text-center p-8 max-w-lg animate-fadeIn">
    <div className="bg-slate-900/80 p-12 rounded-3xl border border-slate-800 backdrop-blur-xl shadow-2xl relative overflow-hidden group">
      <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
      <div className="w-24 h-24 bg-gradient-to-br from-slate-800 to-slate-900 rounded-full flex items-center justify-center mx-auto mb-8 border border-slate-700 shadow-inner">
        <Upload className="w-10 h-10 text-blue-500" />
      </div>
      <h3 className="text-2xl font-bold text-white mb-3">Initialize Analysis</h3>
      <p className="text-slate-400 mb-10 text-sm leading-relaxed">
        Upload panoramic radiograph for full-arch segmentation.
        <br />
        The neural network will map all 32 dentition points.
      </p>
      <label className="inline-flex items-center gap-3 px-10 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl cursor-pointer transition-all shadow-lg hover:shadow-blue-500/30 font-bold tracking-wide transform hover:-translate-y-1">
        <Upload className="w-5 h-5" />
        Load teeth.jpeg
        <input type="file" className="hidden" accept="image/*" onChange={onUpload} />
      </label>
    </div>
  </div>
);

const ProcessingScreen: React.FC = () => (
  <div className="flex flex-col items-center justify-center">
    <div className="relative w-24 h-24 mb-6">
      <div className="absolute inset-0 border-4 border-slate-800 rounded-full" />
      <div className="absolute inset-0 border-4 border-blue-500 rounded-full border-t-transparent animate-spin" />
      <Cpu className="absolute inset-0 m-auto text-blue-500 w-8 h-8 animate-pulse" />
    </div>
    <h3 className="text-xl font-mono text-white font-bold tracking-tight">ANALYZING DENTITION</h3>
    <p className="text-sm text-slate-500 mt-2 font-mono">Mapping 32 anatomical points...</p>
  </div>
);

const PRECISE_PATHS: Record<number, string> = {
  38: 'M 80,60 C 82,58 86,58 89,60 C 90,64 90,68 88,71 C 86,73 83,73 81,71 C 79,69 78,65 79,62 Z',
  46: 'M 22,65 C 23,62 27,62 29,65 L 29,74 L 27,77 L 26,74 L 25,70 L 24,74 L 22,76 Z',
  16: 'M 25,38 C 25,32 27,30 30,30 C 32,32 32,38 30,41 C 27,41 25,38 Z',
  26: 'M 69,38 C 69,32 71,30 74,30 C 76,32 76,38 74,41 C 71,41 69,38 Z',
  48: 'M 11,62 C 12,59 15,59 16,62 L 16,67 C 15,70 12,70 11,67 Z',
};

function getToothPath(id: number, cx: number, cy: number, w: number, h: number) {
  if (PRECISE_PATHS[id]) return PRECISE_PATHS[id];

  const type = id % 10;
  const isLower = id > 30;

  const l = cx - w / 2;
  const r = cx + w / 2;
  const t = cy - h / 2;
  const b = cy + h / 2;

  if (type >= 1 && type <= 2) {
    return `M ${l},${t} L ${r},${t} L ${r - w * 0.1},${b} L ${l + w * 0.1},${b} Z`;
  }
  if (type === 3) {
    return `M ${l},${t + h * 0.2} L ${cx},${t} L ${r},${t + h * 0.2} L ${r - w * 0.1},${b} L ${l + w * 0.1},${b} Z`;
  }
  if (type >= 4 && type <= 5) {
    return `M ${l},${t + h * 0.1} Q ${cx},${t - h * 0.1} ${r},${t + h * 0.1} L ${r},${b - h * 0.2} Q ${cx},${b + h * 0.1} ${l},${b - h * 0.2} Z`;
  }
  if (isLower) {
    return `M ${l},${t} L ${r},${t} L ${r},${b - h * 0.3} L ${r - w * 0.2},${b} L ${cx},${b - h * 0.2} L ${l + w * 0.2},${b} L ${l},${b - h * 0.3} Z`;
  }
  return `M ${l},${t} L ${r},${t} L ${r},${b - h * 0.2} Q ${cx},${b + h * 0.1} ${l},${b - h * 0.2} Z`;
}

function generateClinicalProfile(id: number, condition: string) {
  const profile = {
    crown: 'Intact',
    restorative: 'None',
    pulpal: 'Normal',
    root: 'Normal morphology, dual/single canal anatomy',
    periapical: 'Normal PDL space, no radiolucency',
    perio: 'Normal bone levels, alveolar crest <2mm from CEJ',
    relation: 'Normal contact points',
    flags: [] as string[],
    radiographicEvidence: 'No significant pathology detected.',
    treatment: {
      investigation: 'Routine hygiene',
      conservative: 'N/A',
      surgical: 'N/A',
    },
  };

  if (condition.includes('Impaction') && id === 38) {
    profile.crown = 'Intact, Mesio-angular inclination';
    profile.root = 'Dilacerated distally';
    profile.perio = 'Distal bone loss on 37';
    profile.relation = 'Crown impacting distal cervical of 37';
    profile.flags = ['Impaction-related pathology', 'Structural risk to adjacent tooth'];
    profile.radiographicEvidence = 'Horizontal impaction of 38 detected. Radiolucency at impact site suggests external resorption or caries on 37.';
    profile.treatment = {
      investigation: 'CBCT to assess IAN proximity',
      conservative: 'Monitor (not recommended)',
      surgical: 'Surgical Extraction (Odontectomy) indicated',
    };
  } else if (condition.includes('Impaction') && id === 48) {
    profile.crown = 'Partially erupted';
    profile.perio = 'Pericoronal radiolucency (follicular space)';
    profile.flags = ['Periodontal compromise', 'Soft tissue impaction'];
    profile.radiographicEvidence = 'Vertical impaction. Distal operculum shadow visible.';
    profile.treatment = {
      investigation: 'Clinical probe of pocket depth',
      conservative: 'Irrigation / Hygiene',
      surgical: 'Operculectomy or Extraction if symptomatic',
    };
  } else if (condition.includes('RCT')) {
    profile.crown = 'Full coverage PFM Crown';
    profile.restorative = 'Endodontic obturation present';
    profile.pulpal = 'Endodontically treated';
    profile.periapical = 'PDL widening observed at distal root apex';
    profile.flags = ['Endodontic pathology (Suspected)', 'Structural compromise'];
    profile.radiographicEvidence = 'Radiopaque filling material in canals. Apical seal appears short on mesial root. Widened periodontal ligament space.';
    profile.treatment = {
      investigation: 'Periapical Radiograph (PA)',
      conservative: 'Monitor for symptoms / Re-treatment',
      surgical: 'Apicoectomy if lesion enlarges',
    };
  } else if (condition.includes('Heavily Restored') || condition.includes('Deep Restoration')) {
    profile.crown = 'Large Radiopaque Restoration';
    profile.restorative = 'MOD Amalgam/Composite';
    profile.pulpal = 'Deep caries proximity / Pulp recession';
    profile.flags = ['Caries risk', 'Structural integrity'];
    profile.radiographicEvidence = 'Restorative material extending >2/3 intercuspal width. Close proximity to pulp horns.';
    profile.treatment = {
      investigation: 'Bitewing X-ray + Vitality Test',
      conservative: 'Full coverage crown recommended',
      surgical: 'N/A',
    };
  }

  return profile;
}

const XIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ScanLineIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <path d="M8 12h8" />
    <path d="M12 8v8" />
  </svg>
);

const ClipboardListIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600">
    <path d="M9 2h6a2 2 0 0 1 2 2v2H7V4a2 2 0 0 1 2-2z" />
    <path d="M9 12h6" />
    <path d="M9 16h6" />
    <path d="M7 8h10v12H7z" />
  </svg>
);

const PrinterIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9V2h12v7" />
    <path d="M6 18h12v4H6z" />
    <rect x="6" y="9" width="12" height="7" />
  </svg>
);

export default DentalAnalyzer;
